import { Stack } from "@mui/material";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { useGetQualificationDisplayLabels } from "@src/appV2/Qualifications/api/useGetQualificationDisplayLabels";
import { Badge, PageHeaderWrapper } from "@src/appV2/redesign/system/ui-components";

import { APP_V2_USER_EVENTS, logEvent } from "../../lib/analytics";
import { IconButton } from "../components/IconButton";
import { PickerButton } from "../components/PickerButton";
import { getTimeSlotLabel } from "../Shift/getTimeSlotLabel";
import { ShiftDiscoveryCalloutsContainer } from "./CalloutsContainer";
import { getOverallFilterSummary } from "./Filters/BottomSheet/filterSummaries";
import { useShiftDiscoveryUserFiltersContext } from "./Filters/useUserFiltersContext";
import {
  SHIFT_DISCOVERY_CALENDAR_PATH,
  SHIFT_DISCOVERY_FILTERS_PATH,
  SHIFT_DISCOVERY_WORKPLACES_LIST_MODAL_PATH,
} from "./paths";
import { useGetLocationLabel } from "./useGetLocationLabel";
import { useShiftModalsDataContext } from "./useShiftModalsDataContext";
import { useWorkplaceModalsDataContext } from "./useWorkplaceModalsDataContext";

interface ShiftDiscoveryPageHeaderProps {
  priorityShiftsCount: number;
  urgentShiftsCount: number;
  blockShiftsCount: number;
  inHomeCount?: number;
}

export function ShiftDiscoveryPageHeader(props: ShiftDiscoveryPageHeaderProps) {
  const { priorityShiftsCount, urgentShiftsCount, blockShiftsCount, inHomeCount } = props;
  const { navigateToModal } = useShiftModalsDataContext();
  const { generateModalPath } = useWorkplaceModalsDataContext();

  const { shiftTimeSlots, dates, appliedFiltersCount, licenses, distance } =
    useShiftDiscoveryUserFiltersContext();

  const { data: licenseDisplayLabels } = useGetQualificationDisplayLabels(licenses ?? []);
  const locationLabel = useGetLocationLabel();

  const { primary, secondary } = getOverallFilterSummary({
    shiftTimeSlots,
    dates,
    licenses: licenseDisplayLabels ?? [],
    distance,
    locationLabel,
  });

  const workplacesListPath = generateModalPath(SHIFT_DISCOVERY_WORKPLACES_LIST_MODAL_PATH);

  const isFiltersRedesignEnabled = useCbhFlag(CbhFeatureFlag.SHIFT_DISCOVERY_FILTERS_REDESIGN, {
    defaultValue: false,
  });

  return (
    <PageHeaderWrapper variant="tertiary" sx={{ paddingBottom: 1 }}>
      <ShiftDiscoveryCalloutsContainer
        priorityShiftsCount={priorityShiftsCount}
        urgentShiftsCount={urgentShiftsCount}
        blockShiftsCount={blockShiftsCount}
        inHomeCount={inHomeCount}
        sx={{ px: 5, paddingTop: 1, paddingBottom: 3 }}
      />

      <Stack direction="row" alignItems="center" sx={{ paddingBottom: 3, px: 5 }}>
        {isFiltersRedesignEnabled ? (
          <PickerButton
            elevated
            centered
            fullWidth
            size="medium"
            aria-label="Filter shifts"
            primaryText={primary}
            secondaryText={secondary}
            startIconType="slider-settings"
            isSelected={appliedFiltersCount > 0}
            sx={(theme) => ({
              borderRadius: theme.borderRadius?.large,
              backgroundColor: theme.palette.background.tertiary,
              border: `1px solid ${theme.palette.border?.primary}`,
              marginRight: 3,
              // Override hover state
              "&:hover": {
                backgroundColor: theme.palette.background.tertiary,
                border: `1px solid ${theme.palette.border?.primary}`,
              },
              // Override active/pressed state
              "&:active": {
                backgroundColor: theme.palette.background.tertiary,
                border: `1px solid ${theme.palette.border?.primary}`,
              },
            })}
            onClick={() => {
              logEvent(APP_V2_USER_EVENTS.OPEN_SHIFTS_FILTERS_V2_VIEWED);
              navigateToModal(SHIFT_DISCOVERY_FILTERS_PATH);
            }}
          />
        ) : (
          <>
            <PickerButton
              fullWidth
              size="medium"
              aria-label="Calendar View Navigation"
              primaryText={dates.length === 0 ? "Calendar" : `${dates.length} dates filtered`}
              secondaryText={
                shiftTimeSlots.length === 0 || shiftTimeSlots.length > 2
                  ? "All time slots"
                  : shiftTimeSlots.map((slot) => getTimeSlotLabel(slot)).join(", ")
              }
              startIconType="calendar-navigation"
              isSelected={dates.length > 0}
              sx={{
                "& > *:last-child": {
                  marginRight: 3,
                },
              }}
              onClick={() => {
                logEvent(APP_V2_USER_EVENTS.OPEN_SHIFTS_CALENDAR_OPENED);
                navigateToModal(SHIFT_DISCOVERY_CALENDAR_PATH);
              }}
            />

            <Badge
              count={appliedFiltersCount}
              overlap="circular"
              color="primary"
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <IconButton
                variant="outlinedHeavy"
                aria-label="Open Filters Button"
                size="small"
                iconType="slider-settings"
                isSelected={appliedFiltersCount > 0}
                sx={{ boxShadow: "none !important" }}
                onClick={() => {
                  logEvent(APP_V2_USER_EVENTS.OPEN_SHIFTS_FILTERS_VIEWED);
                  navigateToModal(SHIFT_DISCOVERY_FILTERS_PATH);
                }}
              />
            </Badge>
          </>
        )}

        <IconButton
          variant="outlinedHeavy"
          aria-label="Workplaces Search"
          size="small"
          iconType="building"
          sx={{ boxShadow: "none !important" }}
          href={workplacesListPath}
          onClick={() => {
            logEvent(APP_V2_USER_EVENTS.BROWSE_WORKPLACES_OPENED);
          }}
        />
      </Stack>
    </PageHeaderWrapper>
  );
}
